// C:\Users\phill\paperplay\frontend\src\features\account\store\accountSlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AccountState, User, UserActivityDetails } from '../accountTypes';
import type { PermissionName } from '../../../types/commonTypes';
import {
  fetchAccountCurrentUser,
  fetchAccountUserActivities,
  fetchAccountUserPermissions,
} from './accountThunks';

/* ----------------------------------------------------------------------------
 * Initial state
 * -------------------------------------------------------------------------- */

const initialState: AccountState = {
  id: null,
  user: null,
  currentUser: null,
  isAuthenticated: false,

  userActivities: [],
  user_permissions: [],

  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalCount: 0,
  },

  loading: false,
  error: null,
};

/* ----------------------------------------------------------------------------
 * Slice
 * -------------------------------------------------------------------------- */

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    /** Local update when we already have the user (e.g., after login flow elsewhere). */
    setCurrentUser(state, action: PayloadAction<User | null>) {
      const u = action.payload;
      state.currentUser = u;
      state.user = u;
      state.id = u?.userId ?? null;
      state.isAuthenticated = !!u;
    },
    /** Merge a partial user patch (e.g., after editing profile fields). */
    mergeCurrentUser(state, action: PayloadAction<Partial<User>>) {
      if (!state.currentUser) return;
      state.currentUser = { ...state.currentUser, ...action.payload };
      state.user = state.currentUser;
    },
    /** Clear error banner/state. */
    clearAccountError(state) {
      state.error = null;
    },
    /** Full sign‑out reset. */
    logoutAccount() {
      return { ...initialState };
    },
  },
  extraReducers: builder => {
    /* -------------------- fetchAccountCurrentUser -------------------- */
    builder
      .addCase(fetchAccountCurrentUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        const u = action.payload;
        state.loading = false;
        state.currentUser = u;
        state.user = u;
        state.id = u?.userId ?? null;
        state.isAuthenticated = !!u?.userId;
      })
      .addCase(fetchAccountCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load current user.';
        state.isAuthenticated = false;
      });

    /* ----------------- fetchAccountUserPermissions ------------------- */
    builder
      .addCase(fetchAccountUserPermissions.pending, state => {
        state.error = null;
      })
      .addCase(
        fetchAccountUserPermissions.fulfilled,
        (state, action: PayloadAction<PermissionName[]>) => {
          state.user_permissions = action.payload ?? [];
        },
      )
      .addCase(fetchAccountUserPermissions.rejected, (state, action) => {
        state.error = action.payload || 'Failed to load permissions.';
      });

    /* ------------------- fetchAccountUserActivities ------------------ */
    builder
      .addCase(fetchAccountUserActivities.pending, state => {
        state.error = null;
        // Keep single "loading" simple; if you want granular flags, split later.
        state.loading = true;
      })
      .addCase(
        fetchAccountUserActivities.fulfilled,
        (
          state,
          action: PayloadAction<{
            items: UserActivityDetails[];
            page: number;
            pageSize: number;
            totalPages: number;
            totalCount: number;
          }>,
        ) => {
          const { items, page, pageSize, totalPages, totalCount } = action.payload;
          state.loading = false;
          state.userActivities = items || [];
          state.pagination = {
            currentPage: page,
            pageSize,
            totalPages,
            totalCount,
          };
        },
      )
      .addCase(fetchAccountUserActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load activities.';
      });
  },
});

export const { setCurrentUser, mergeCurrentUser, clearAccountError, logoutAccount } =
  accountSlice.actions;

export default accountSlice.reducer;
